import React, { useEffect, useState } from "react";
import { HiArrowLeft } from "react-icons/hi2";
import { usePublicPageSection } from "../../../../hooks/usePublicPageSection";
import { getCaseById } from "../../../../api/casesApi";
import { resolveMediaUrl } from "../../../../utils/mediaUrl";
import { getApiErrorMessage } from "../../../../utils/apiResponse";
import { useLanguage } from "../../../../context/LanguageContext";
import "./CaseResult.css";

const requireField = (fields, key) => {
  const value = fields?.[key];
  if (value == null || String(value).trim() === "") {
    throw new Error(
      `Case result field "${key}" is missing from the API response.`
    );
  }
  return value;
};

const requireMediaUrl = (section, groupKey) => {
  const item = section.media?.groups?.[groupKey]?.[0] || null;
  if (!item?.url) {
    throw new Error(
      `Case result media group "${groupKey}" is missing from the API response.`
    );
  }
  return resolveMediaUrl(item.url);
};

const buildChrome = (section) => {
  const f = section.fields || {};
  return {
    backButton: requireField(f, "backButton"),
    caseBadge: requireField(f, "caseBadge"),
    submittedLabel: requireField(f, "submittedLabel"),
    updatedLabel: requireField(f, "updatedLabel"),
    categoryLabel: requireField(f, "categoryLabel"),
    institutionLabel: requireField(f, "institutionLabel"),
    progressLabel: requireField(f, "progressLabel"),
    currentBadge: requireField(f, "currentBadge"),
    stepTitles: {
      received: requireField(f, "step1Title"),
      "in-review": requireField(f, "step2Title"),
      resolved: requireField(f, "step3Title"),
    },
    pendingMeta: requireField(f, "step3Meta"),
    securityText: requireField(f, "securityText"),
    shieldIcon: requireMediaUrl(section, "shield"),
  };
};

const formatDate = (value, language) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(
    language === "ar" ? "ar" : language === "ku" ? "ku" : "en-GB",
    { day: "numeric", month: "long", year: "numeric" }
  ).format(date);
};

const CaseResult = ({ caseId, onBack }) => {
  const { language, meta } = useLanguage();
  const {
    model: chrome,
    error: sectionError,
    isLoading: pageLoading,
  } = usePublicPageSection("track", "case-result", buildChrome);
  const [caseData, setCaseData] = useState(null);
  const [caseError, setCaseError] = useState("");
  const [caseLoading, setCaseLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setCaseLoading(true);
      setCaseError("");
      try {
        const tracked = await getCaseById(caseId, language);
        if (!cancelled) {
          setCaseData(tracked);
          setCaseError("");
        }
      } catch (err) {
        if (!cancelled) {
          setCaseError(getApiErrorMessage(err));
          setCaseData(null);
        }
      } finally {
        if (!cancelled) setCaseLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [language, caseId]);

  const loading = (pageLoading && !chrome) || (caseLoading && !caseData);
  const error = sectionError || caseError;

  if (loading) {
    return (
      <section className="case-result" dir={meta.dir}>
        <div className="case-result__container">
          <p style={{ color: "#64748b", fontSize: 14 }}>Loading…</p>
        </div>
      </section>
    );
  }

  if (error || !chrome || !caseData) {
    return (
      <section className="case-result" dir={meta.dir}>
        <div className="case-result__container">
          <button type="button" className="case-result__back" onClick={onBack}>
            <HiArrowLeft aria-hidden="true" />
            <span>Back</span>
          </button>
          <p style={{ color: "#b91c1c", fontSize: 14 }}>
            {error || "Case result unavailable."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="case-result" dir={meta.dir}>
      <div className="case-result__container">
        <button type="button" className="case-result__back" onClick={onBack}>
          <HiArrowLeft aria-hidden="true" />
          <span>{chrome.backButton}</span>
        </button>

        <article className="case-result__card">
          <div className="case-result__header">
            <div className="case-result__header-main">
              <span className="case-result__case-badge">{chrome.caseBadge}</span>
              <div className="case-result__id-row">
                <h1 className="case-result__id">{caseData.caseId}</h1>
                <span className="case-result__status">
                  <span className="case-result__status-dot" aria-hidden="true" />
                  {caseData.statusLabel}
                </span>
              </div>
            </div>
          </div>

          <div className="case-result__details">
            <div className="case-result__detail">
              <span className="case-result__detail-label">
                {chrome.submittedLabel}
              </span>
              <span className="case-result__detail-value">
                {formatDate(caseData.submittedAtUtc, language)}
              </span>
            </div>
            <div className="case-result__detail">
              <span className="case-result__detail-label">
                {chrome.updatedLabel}
              </span>
              <span className="case-result__detail-value">
                {formatDate(caseData.updatedAtUtc, language)}
              </span>
            </div>
            <div className="case-result__detail">
              <span className="case-result__detail-label">
                {chrome.categoryLabel}
              </span>
              <span className="case-result__detail-value">
                {caseData.category}
              </span>
            </div>
            <div className="case-result__detail">
              <span className="case-result__detail-label">
                {chrome.institutionLabel}
              </span>
              <span className="case-result__detail-value">
                {caseData.institution}
              </span>
            </div>
          </div>

          <div className="case-result__progress">
            <p className="case-result__progress-label">{chrome.progressLabel}</p>

            <ol className="case-result__timeline">
              {(caseData.progress || []).map((step) => {
                const stateClass =
                  step.state === "done"
                    ? "case-result__step--done"
                    : step.state === "current"
                      ? "case-result__step--current"
                      : "case-result__step--pending";
                const title =
                  chrome.stepTitles[step.key] || step.key;
                const metaText =
                  step.state === "pending"
                    ? chrome.pendingMeta
                    : step.meta
                      ? `${step.meta}${
                          step.atUtc
                            ? ` · ${formatDate(step.atUtc, language)}`
                            : ""
                        }`
                      : step.atUtc
                        ? formatDate(step.atUtc, language)
                        : "";

                return (
                  <li
                    key={step.key}
                    className={`case-result__step ${stateClass}`}
                  >
                    <span className="case-result__marker" aria-hidden="true">
                      {step.state === "done" ? (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            d="M10 3L4.5 8.5L2 6"
                            stroke="white"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <span className="case-result__marker-core" />
                      )}
                    </span>
                    <div className="case-result__step-copy">
                      <div className="case-result__step-title-row">
                        <p className="case-result__step-title">{title}</p>
                        {step.state === "current" && (
                          <span className="case-result__current-badge">
                            {chrome.currentBadge}
                          </span>
                        )}
                      </div>
                      {metaText && (
                        <p className="case-result__step-meta">{metaText}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="case-result__security">
            <img src={chrome.shieldIcon} alt="" aria-hidden="true" />
            <p>{chrome.securityText}</p>
          </div>
        </article>
      </div>
    </section>
  );
};

export default CaseResult;
